import { Mutex } from '@aws-amplify/core';
import {
  LogParser,
  StartStreamResponse_t,
  StreamResponse_t,
  StreamStatus_t,
  StreamStatusResponse_t,
} from 'foreman-slink';
import { Selector } from 'react-redux';
import { JobLog } from 'src/models/jobLog';
import { selectNetworkState } from 'src/redux/common/selectors';
import { logActions } from 'src/redux/log/actions';
import { createLogLocalDataByFilenameSelector } from 'src/redux/log/selectors';
import { LocalJobLogData } from 'src/redux/log/types';
import { store } from 'src/redux/store';
import { AppState } from 'src/redux/types';
import { logsApiLiveSync, type LiveSyncRequest } from 'src/utils/api/logsApi';
import { getLimitedMode } from 'src/utils/auth/getLimitedMode';
import { logParserDataToJobLogV2 } from 'src/utils/logs/logHelpers';
import { socketClientSentinel } from 'src/utils/socket/context/sentinelSocketContext';
import { publishMqttMessage } from 'src/ble/MqttClient';

/** The timestamp value (in ms elapsed) that we consider to be the "setup" phase of job logs. This is used for replay detection. */
const TIMESTAMP_SETUP_CUTOFF = 10000;

/**
 * Manages a live job log stream for a single log file (instances are not reusable between log files).
 * This service handles the following responsibilities:
 * - Dispatching redux actions when events are received, so that the application can display the live data.
 * - Responding to the controller for all received events.
 */
export class LiveJobLogService {
  private parser: LogParser = new LogParser();
  /** The filename of the log that was opened. */
  private readonly filename: string;
  /** The serial number of the controller that opened the log. */
  private readonly serialNumber: string;
  /** Whether the event queue is currently being processed. */
  private readonly eventQueueLock = new Mutex();
  /** The setInterval handle for the event queue. */
  private eventQueueIntervalHandle!: number;
  /** The setInterval handle for the MQTT event queue. */
  private eventQueueMqttIntervalHandle!: number;
  /** Whether the log has been marked as closed. We will not try and sync data when this is true. */
  private isClosed: boolean = false;
  /** Indicates whether the log data is in a potentially inconsistent state, and we have requested a replay. */
  private didRequestReplay: boolean = false;
  /** Track whether interval to cloud is active. If isPaused is true, there is no interval */
  private isPaused: boolean = false;

  private readonly logSelector: Selector<AppState, LocalJobLogData | undefined>;

  private get logData(): LocalJobLogData | undefined {
    return this.logSelector(store.getState());
  }

  private get logId(): string | undefined {
    return this.logData?.id;
  }

  /** Whether this job log still needs to be opened in the sentinel system (before syncing can begin). */
  private get pendingOpen(): boolean {
    return !this.logId;
  }

  /** Whether this job log needs to have its metadata updated in the sentinel system. */
  private get pendingUpdateMetadata(): boolean {
    return !!this.logData?.metadata;
  }

  /** Whether this job log still needs to be closed in the sentinel system. */
  private get pendingClose(): boolean {
    return this.logData?.pendingClose ?? false;
  }

  /**
   * @param filename The filename associated with the log file.
   * @param serialNumber The serial number associated with the controller that opened the log file.
   * @param skipMetadataCollection
   */
  constructor(filename: string, serialNumber: string, skipMetadataCollection: boolean) {
    this.filename = filename;
    this.serialNumber = serialNumber;
    this.logSelector = createLogLocalDataByFilenameSelector(this.filename);

    if (this.logData) {
      // If we have cached redux data (for example the app has restarted),
      // initialize the internal parser with those lines.
      let hasError = false;
      for (const line of this.logData.raw.split('\n')) {
        try {
          this.parser.Parse(line);
        } catch (e) {
          // console.warn(`[LiveJobLogService] ${this.filename} Failed to parse line`, line, e);
          hasError = true;
        }
      }
      const data = logParserDataToJobLogV2(this.parser.data);
      store.dispatch(
        logActions.liveJobLogProgress({
          filename: this.filename,
          data,
          error: hasError,
          raw: '',
        }),
      );
    } else {
      // Otherwise, this is a new job log, initialize it in redux.
      store.dispatch(
        logActions.liveJobLogOpen({
          filename: this.filename,
          serialNumber: this.serialNumber,
          skipMetadataCollection,
        }),
      );

      if (localStorage.getItem('filename') === this.filename) {
        // Restart processing of an existing job
      } else {
        // Initialize the Sentinel Max Job Segment ID.
        // Wait for it to be populated in LogDetailsFormModal.tsx before starting MQTT streaming in handleSyncMqtt below.
        // This ensures a Job Segment ID has been selected in the modal form.
        localStorage.setItem('sentinelMaxJobId', '');
        localStorage.setItem('sentinelMaxJobSegmentId', '');
        localStorage.setItem('filename', this.filename);
      }
    }

    if (!this.logData) throw new Error(`Failed to initialize LiveJobLogService for log file ${filename}`); // Sanity check

    this.setQueueInterval();
    this.setQueueMqttInterval();

    void this.processQueue();
  }

  /** Called when controller sends the start event for the streaming log. */
  public readonly onStreamStart = (event: StartStreamResponse_t) => {
    console.log('[LiveJobLogService] Stream started!', this.filename);
    this.respondStatus(event);
  };

  /** Called when the controller sends a line for the currently streaming log. */
  public readonly onStreamData = (event: StreamResponse_t) => {
    try {
      const eventLines: string[] = event.payload_data.split(/\r?\n/);

      let isReplayDetected = false;
      const retrogradeTimestampIndices: number[] = [];
      const preprocessedEventLines = preprocessEventLines(
        eventLines,
        () => (isReplayDetected = true),
        eventLineIndex => retrogradeTimestampIndices.push(eventLineIndex),
        this.logData,
      );

      if (isReplayDetected) {
        console.log('[LiveJobLogService] Start of stream detected!', this.filename, preprocessedEventLines);
        this.parser = new LogParser();
        store.dispatch(logActions.liveJobLogReplayDetected({ filename: this.filename }));
        this.didRequestReplay = false;
      } else {
        console.log(
          '[LiveJobLogService] Removing each event that has a retrograde timestamp',
          this.filename,
          preprocessedEventLines,
        );
        retrogradeTimestampIndices.forEach((unexpectedTimestampIndex, numberOfLinesRemovedSoFar) => {
          preprocessedEventLines.splice(unexpectedTimestampIndex - numberOfLinesRemovedSoFar, 1);
        });
      }

      let hasError = false;
      for (const line of preprocessedEventLines) {
        try {
          this.parser.Parse(line);
        } catch (e) {
          // console.warn(`[LiveJobLogService] ${this.filename} Failed to parse line`, line, e);
          hasError = true;
        }
      }

      const data = logParserDataToJobLogV2(this.parser.data);
      store.dispatch(
        logActions.liveJobLogProgress({
          filename: this.filename,
          data,
          error: hasError,
          raw: eventLines.join('\n'),
        }),
      );
      this.respondStatus(event);
    } catch (e) {
      console.error(`[LiveJobLogService] onStreamData failed for file`, this.filename, e);
      event.streamStatus(StreamStatus_t.error);
    }
  };

  /** Called when the controller requests that the application send it the current live stream status. */
  public readonly onStreamStatus = (event: StreamStatusResponse_t) => {
    this.respondStatus(event);
  };

  /** Marks the log file as closed. */
  public readonly close = () => {
    store.dispatch(logActions.liveJobLogClose({ filename: this.filename }));
    void this.processQueue();
    void this.handleSyncMqtt();
  };

  /** Called on error or completion of job */
  public readonly destroy = () => {
    this.isClosed = true;
    window.clearInterval(this.eventQueueIntervalHandle);
    window.clearInterval(this.eventQueueMqttIntervalHandle);
  };

  /** Called when disconnected from controller. Stops the 3-min interval and sends latest data to cloud */
  public readonly pause = () => {
    this.isPaused = true;
    window.clearInterval(this.eventQueueIntervalHandle);
    window.clearInterval(this.eventQueueMqttIntervalHandle);
    void this.processQueue();
    void this.handleSyncMqtt();
  };

  /** Called when BleControllerService is instantiated. Resumes 3-min interval if we are in paused state. */
  public readonly unpause = () => {
    if (this.isPaused) {
      this.setQueueInterval();
      this.setQueueMqttInterval();
    }
    this.isPaused = false;
  };

  // Process the queue (mobile stream to S3) once per three minutes.
  private readonly setQueueInterval = () => {
    this.eventQueueIntervalHandle = window.setInterval(
      () => {
        void this.processQueue();
      },
      1000 * 60 * 3,
    );
  };

  // Process the queue (mobile stream to SentinelMax via MQTT)
  private readonly setQueueMqttInterval = () => {
    this.eventQueueMqttIntervalHandle = window.setInterval(() => {
      void this.handleSyncMqtt();
    }, 5000);
  };

  private readonly processQueue = async () => {
    const limitedMode = await getLimitedMode();
    if (!!limitedMode) return;
    if (!socketClientSentinel.socket.connected) return;
    if (this.eventQueueLock.isLocked() || this.isClosed) return;

    const releaseLock = await this.eventQueueLock.acquire();
    try {
      if (this.pendingOpen) await this.handleOpen();
      if (this.pendingOpen) {
        this.destroy();
        throw new Error('Did not successfully open log file!'); // Sanity check
      }
      if (this.pendingUpdateMetadata) await this.handleUpdateMetadata();
      await this.handleSync();
      if (this.pendingClose) await this.handleClose();
      if (this.pendingClose) {
        this.destroy();
        throw new Error('Did not successfully close log file!'); // Sanity check
      }
    } catch (e) {
      console.error(`[LiveJobLogService] Error processing LiveJobLogService ${this.filename} event queue`, e);
    } finally {
      releaseLock();
    }
  };

  /**
   * Responds to the controller with the current network status. All live stream events require a response to the controller.
   * @param event The event object that can be used to respond to the controller.
   */
  private readonly respondStatus = (event: StreamStatusResponse_t) => {
    const status = this.getStreamStatus();
    if (status !== StreamStatus_t.open) {
      console.log('[LiveJobLogService] Reporting status:', status);
    }
    if (status === StreamStatus_t.openNewFile) {
      store.dispatch(logActions.liveJobLogReplayRequested({ filename: this.filename }));
      this.didRequestReplay = true;
    }
    event.streamStatus(status);
  };

  /** Returns the current stream status to report the controller. */
  private readonly getStreamStatus = (): StreamStatus_t => {
    const liveLog = this.logSelector(store.getState());
    if (!liveLog) return StreamStatus_t.error;
    if (this.isClosed) return StreamStatus_t.closed;

    const needsReplay =
      !this.didRequestReplay &&
      !!liveLog.raw &&
      getTimestampForLogText(liveLog.raw.split('\n')[0]) > TIMESTAMP_SETUP_CUTOFF;
    // We instruct the controller to replay the log when our first line has a timestamp (in ms elapsed) of over 10s (we expect the first line to have a timestamp of 0 or 1, generally).
    if (needsReplay) return StreamStatus_t.openNewFile;

    const network = selectNetworkState(store.getState());
    if (network === 'none' || network === 'unknown') return StreamStatus_t.openNoInternet;
    return StreamStatus_t.open;
  };

  /**
   * Handles the 'open' queue event. Searches for a matching job log in the Sentinel system, or creates a new one if not found.
   */
  private readonly handleOpen = async () => {
    try {
      console.log(`[LiveJobLogService] Handling open for filename:`, this.filename);
      store.dispatch(logActions.liveJobLogInitializeStart({ filename: this.filename }));
      const res = await socketClientSentinel.request<JobLog>('logs live-open-v2', {
        fileName: this.filename,
        serialNumber: this.serialNumber,
        deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      store.dispatch(logActions.liveJobLogInitializeSuccess({ filename: this.filename, info: res }));
      console.log(`[LiveJobLogService] Completed open for filename:`, this.filename);
    } catch (e) {
      store.dispatch(
        logActions.liveJobLogInitializeFailure({
          filename: this.filename,
          error: e instanceof Error ? e.message : undefined,
        }),
      );
      throw e;
    }
  };

  /**
   * Handles the 'update metadata' queue event. Updates information about the job log in the sentinel system.
   */
  private readonly handleUpdateMetadata = async () => {
    try {
      console.log(`[LiveJobLogService] Handling update metadata for filename:`, this.filename);
      store.dispatch(logActions.liveJobLogUpdateMetadataStart({ filename: this.filename }));
      if (!this.logId) throw new Error('Attempted to update job log metadata, but no id was ever set');
      const metadata = this.logData?.metadata;
      if (!metadata) throw new Error(`Attempted to update metadata, but no metadata was set for log: ${this.filename}`);

      const updateData: Partial<JobLog> = {
        description: metadata.description,
      };
      if (metadata.siteName) updateData.siteName = metadata.siteName;
      if (metadata.heatExchangerModelNumber) updateData.heatExchangerModelNumber = metadata.heatExchangerModelNumber;
      if (metadata.bundleId) updateData.bundleId = metadata.bundleId;
      if (metadata.timezone) updateData.deviceTimezone = metadata.timezone;

      await socketClientSentinel.request<JobLog>('logs update', {
        _id: this.logId,
        update: { $set: updateData },
      });

      store.dispatch(logActions.liveJobLogUpdateMetadataSuccess({ filename: this.filename }));
      console.log(`[LiveJobLogService] Completed update metadata for filename:`, this.filename);
    } catch (e) {
      store.dispatch(
        logActions.liveJobLogUpdateMetadataFailure({
          filename: this.filename,
          error: e instanceof Error ? e.message : undefined,
        }),
      );
      throw e;
    }
  };

  /**
   * Handles the 'sync' queue event. Transmits all data to the sentinel service that has not yet been sent.
   */
  private readonly handleSync = async () => {
    try {
      /**
       * TODO: calculate diff with server
       * If client is behind server, but does not diverge, do nothing.
       * If client is ahead of server, but does not diverge, append missing lines
       * If client and server diverge, send everything from client to replace server
       */
      console.log('[LiveJobLogService] Handling sync for filename:', this.filename);
      store.dispatch(logActions.liveJobLogSyncStart({ filename: this.filename }));
      if (!this.logData?.id) throw new Error('Attempted to sync job log, but was never initialized!');
      const body: LiveSyncRequest = {
        id: this.logData.id,
        data: {
          strategy: 'replace-lines',
          lines: this.logData.raw.split(/\r?\n/),
        },
      };
      await logsApiLiveSync(body);

      store.dispatch(logActions.liveJobLogSyncSuccess({ filename: this.filename }));
      console.log('[LiveJobLogService] Completed sync for filename:', this.filename);
    } catch (e) {
      store.dispatch(logActions.liveJobLogSyncFailure({ filename: this.filename }));
      throw e;
    }
  };

  /**
   * Handles the 'close' queue event.
   */
  private readonly handleClose = async () => {
    try {
      console.log('[LiveJobLogService] Handling close for filename:', this.filename);
      store.dispatch(logActions.liveJobLogFinalizeStart({ filename: this.filename }));
      if (!this.logId) throw new Error('Attempted to close job log, but no id was ever set!');
      await socketClientSentinel.request('logs live-close-v2', { id: this.logId });

      this.destroy();
      store.dispatch(logActions.liveJobLogFinalizeSuccess({ filename: this.filename }));
      console.log('[LiveJobLogService] Completed close for filename:', this.filename);
    } catch (e) {
      store.dispatch(logActions.liveJobLogInitializeFailure({ filename: this.filename }));
      throw e;
    }
  };

  // *******************************************************************************************
  // Transmit logfile delta to SentinelMax via MQTT
  // *******************************************************************************************
  private readonly handleSyncMqtt = () => {
    try {
      // Only process if a Job Segment ID has been selected
      // and processing is for the last "Live" status job
      if (localStorage.getItem('sentinelMaxJobSegmentId') && localStorage.getItem('filename') === this.filename) {
        // Get the processed logfile rows
        store.dispatch(logActions.liveJobLogSyncStart({ filename: this.filename }));
        if (!this.logData?.id) throw new Error('Attempted to sync job log, but was never initialized!');
        const body: LiveSyncRequest = {
          id: this.logData.id,
          data: {
            strategy: 'replace-lines',
            lines: this.logData.raw.split(/\r?\n/),
          },
        };
        console.log('*** [LiveJobLogService.handleSyncMqtt] Logfile Rows:', body.data.lines.length);

        // Check for new logfile
        const logLastFilename = localStorage.getItem('logLastFilename') || '';
        if (logLastFilename !== this.filename) {
          localStorage.setItem('logLastFilename', this.filename);
          localStorage.setItem('logLastRowProcessed', '-1');
        }

        // Check for new rows to process
        const endRow = body.data.lines.length - 1;
        if (endRow > Number(localStorage.getItem('logLastRowProcessed'))) {
          // Get Message Payload
          const startRow = Number(localStorage.getItem('logLastRowProcessed')) + 1;
          const payloadSlice = body.data.lines.slice(startRow, body.data.lines.length);
          //const mqttPayload = payloadSlice.join('\n');

          // Send payload
          publishMqttMessage(
            'raw/' + localStorage.getItem('sentinelMaxJobSegmentId'), // raw/<jobSegmentID
            payloadSlice,
            this.filename,
            this.serialNumber,
            localStorage.getItem('accessToken') || '',
            false,
            startRow,
          );

          // Set last row processed for processing future processing
          localStorage.setItem('logLastRowProcessed', endRow.toString());
        }
      }
    } catch (err: any) {
      console.error('[LiveJobLogService] handleSyncMqtt Error: ', err.message);
    }
  };
}

/**
 * Preprocess incoming event lines from Sentinel handheld controller in one pass.
 *
 * Preprocessing includes:
 *
 * 1) Tossing out lines that are the empty string.
 * 2) Removing any null terminators.
 * 3) Detecting which lines, if any, have a timestamp earlier than the most recent timestamp received. We'll call this a
 *    "retrograde timestamp".
 * 4) Detecting if a file was streamed and then streamed again. We'll call this a "replay".
 *
 * #1 and #2 are straightforward, but the other two deserve comment.
 *
 * Under normal operation, if a retrograde timestamp is detected, then that event must be ignored. Otherwise, users
 * may see a live job (or a streamed file) go back in time.
 *
 * However, if a replay has occurred, then retrograde timestamps are valid and should not be ignored.
 *
 * The purpose of this function is to detect and report these two cases, but not take any other action for them. It is
 * the responsibility of the caller to note they have happened and take appropriate action. Note that this means
 * that all retrograde timestamps will be reported, even those where a replay has been detected. The caller may choose
 * to not make use of the retrograde timestamp indexes in this case, but it seems best to always report all of them for
 * consistency.
 *
 * @param eventLines
 * @param onReplayDetected
 * @param onRetrogradeTimestamp
 * @param logData
 * @private
 */
export const preprocessEventLines = (
  eventLines: (string | undefined | null)[],
  onReplayDetected: () => void,
  onRetrogradeTimestamp: (eventLineIndex: number) => void,
  logData: any | undefined,
): string[] => {
  const preprocessedEventLines: string[] = [];
  eventLines.forEach(eventLine => {
    if (!eventLine) return;

    // Remove null terminator the Sentinel handheld controller uses as a message ending and add to preprocessed lines.
    preprocessedEventLines.push(eventLine.trim().replaceAll('\0', ''));

    // If there's no local log data, treat it as a new log and move on to the next item.
    if (!logData.raw) {
      onReplayDetected();
      return;
    }

    const lineTimestamp = getTimestampForLogText(eventLine);
    const latestTimestamp = getTimestampForLogText(logData.raw);

    // Timestamp is not retrograde, move on to the next event line.
    if (lineTimestamp >= latestTimestamp) return;

    // Invalid timestamp, move on to the next event line.
    if (lineTimestamp === -1 || latestTimestamp === -1) return;

    onRetrogradeTimestamp(preprocessedEventLines.length - 1);

    if (lineTimestamp <= TIMESTAMP_SETUP_CUTOFF) {
      onReplayDetected();
    } else {
      console.error(
        `[LiveJobLogService] Received a log line that happened at ${lineTimestamp}ms, but expected either a value greater than or equal to ${latestTimestamp} to continue the log, or a value less than ${TIMESTAMP_SETUP_CUTOFF} to reset the log. The log is very likely in an inconsistent state.`,
      );
    }
  });

  return preprocessedEventLines;
};

/**
 * For a provided chunk of log text, returns the timestamp (in ms elapsed) of the last line.
 *
 * @param text The log text to parse.
 * @returns The ms elapsed value of the last line in the log text. If no number can be detected, returns -1.
 */
const getTimestampForLogText = (text: string): number => {
  try {
    let match = /_\[(\d+)].*$/i.exec(text);
    if (!match || !match[1]) {
      match = /<\[(\d+)].*$/i.exec(text);
      if (!match || !match[1]) {
        throw new Error('[LiveJobLogService] Failed to get timestamp for log text!');
      }
    }

    const parsed = Number.parseInt(match[1], 10);
    if (Number.isNaN(parsed))
      throw new Error(`[LiveJobLogService] Timestamp from log text was not a number! Got: ${match[1]}`);
    return parsed;
  } catch (e) {
    console.error(e);
    return -1;
  }
};

if (module.hot) {
  // This can't be hot-reloaded, trigger a full refresh
  module.hot.decline();
}
